import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DocumentationInterface, FormField, NewFormField, MessageProps, AssistantResponse, UserInterface, OwnerInterface } from '../types';
import useApi from '../api';
import Schema from './Schema';
import SchemaEdit from './SchemaEdit';
import DocTree from './DocTree';
import CreateChat from '../chat/CreateChat';
import Chat from '../chat/Chat';
import { render } from '@testing-library/react';
import { list } from 'postcss';
import Spinner from "./Spinner"
import { ToastContainer, toast, ToastOptions } from 'react-toastify';
import toastConfig from '../config/Toast';
import 'react-toastify/dist/ReactToastify.css';
import DocForm from './DocForm';
import DocUpload from './DocUpload';
import DocUploadText from './DocUploadText';
import { add, set } from 'lodash';
import { idText } from 'typescript';
import { 
    getFieldByPath, updateFormFieldValue, insertSubSchema, 
    removeSubSchema, createListItem, readListItem, 
    updateListItem, removeListItem, clearListSchema, 
    addField, editField, deleteField, updateFieldOrder, 
    initializeFormFields 
    
} from './doc_utils';


const DocMini = ({ document } : { document: DocumentationInterface}) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [schema, setSchema] = useState('');
    const [isNewDoc, setNewDoc] = useState(false);
    const [docDescription, setDocDescription] = useState('');
    const [formFields, setFormFields] = useState<FormField[]>([]);
    const [docForm, setDocForm] = useState<any>(null);
    const [docName, setDocName] = useState('');
    const [owner, setOwner] = useState<OwnerInterface>();
    const [consumers, setConsumers] = useState<OwnerInterface[]>([]);
    const [docId, setDocId] = useState(document.id);
    const [status, setStatus] = useState('');
    const [needsUpdate, setNeedsUpdate] = useState(false);
    const [ownerNeedsUpdate, setOwnerNeedsUpdate] = useState(false);
    const [consumerNeedsUpdate, setConsumerNeedsUpdate] = useState(false);
    const [userInput, setUserInput] = useState('');
    const [toggleEdit, setToggleEdit] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [initiateChat, setInitiateChat] = useState(false);
    const [conversation, setConversation] = useState<any>();
    const [changes, setChanges] = useState({
        value_modified: [],
        field_modified: [],
        field_deleted: [],
        list_item_added: [],
        list_item_deleted: [],
        list_item_updated: []
    });

    const { get, getStream, post, postStream, put, patch, remove } = useApi();

    // get query params
    const queryParams = new URLSearchParams(window.location.search);
    const conversationId = queryParams.get('conversationId');
    const newDoc = queryParams.get('new');

    // create formFields ref
    const formFieldsRef = React.useRef(formFields);
    formFieldsRef.current = formFields;

    // create changes ref
    const changesRef = React.useRef(changes);
    changesRef.current = changes;

    // Fetch the doc details using the id from the URL params
    const fetchDocumentation = async (document: DocumentationInterface) => {
        try {
            setLoading(true);
            setFetching(true);
            // const response = await get(`/documentation/${paramId || docId}`);

            // const data: DocumentationInterface = response.data;

            setDocDescription(document.description);
            if (newDoc === 'true') {
                setFormFields(initializeFormFields(document.doc_schema ? document.doc_schema : []));
            } else {
                setFormFields(document.doc_schema ? document.doc_schema : []);
            }
            setDocName(document.name);
            setDocForm(document.doc_form);
            setStatus(document.status);
            setOwner(document.owner);
            setConsumers(document.consumers ? document.consumers : [])
        } catch (error) {
            console.error('Error fetching products:', error);
            toast.error(`Error getting documentation.`, toastConfig);
        }
        setLoading(false);
        setFetching(false);
    };

    const handleSubmit = async (e?: any) => {
        if (e) e.preventDefault();

        let response: any;
        const payload = {
            name: docName,
            description: docDescription,
            status: status,
            doc_schema: formFieldsRef.current,
            doc_form: docForm,
            changes: changesRef.current
        };

        try {

        response = await put(`/documentation/${docId}`, payload);
        setFormFields(response.data.doc_schema);
        setChanges({
            value_modified: [],
            field_modified: [],
            field_deleted: [],
            list_item_added: [],
            list_item_deleted: [],
            list_item_updated: []
        });

            toast.success(`${docName} succesfully updated.`, toastConfig);

        } catch (error) {
            console.error('Error:', error);
            toast.error(`${docName} failed to update.`, toastConfig);
        }
        setLoading(false);
    };

    const updateOwner = async (owner: OwnerInterface) => {
        try {
            setLoading(true);
            const payload = {
                owner_id: owner.id,
                owner_type: owner.type,
                owner_name: owner.name
            }
            const response = await put(`/documents/${docId}/owner`, payload);
            setOwner(owner);
            toast.success(`Owner succesfully updated.`, toastConfig);
        } catch (error) {
            console.error('Error:', error);
            toast.error(`${docName} failed to update.`, toastConfig);
        }
        setLoading(false);
    };
    // set owner if changed

    const addConsumer = async (consumer: OwnerInterface) => {
        try {
            setLoading(true);
            const payload = {
                consumer_id: consumer.id,
                consumer_type: consumer.type,
                consumer_name: consumer.name
            }
            const response = await put(`/documents/${docId}/consumers`, payload);
            setConsumers([...consumers, consumer]);
            toast.success(`Consumers succesfully updated.`, toastConfig);
        } catch (error) {
            console.error('Error:', error);
            toast.error(`${docName} failed to update.`, toastConfig);
        }
        setLoading(false);
    };

    const removeConsumer = async (consumer: OwnerInterface) => {
        try {
            setLoading(true);
            // const payload = {
            //     consumer_id: consumer.id,
            //     consumer_type: consumer.type,
            //     consumer_name: consumer.name
            // }
            const response = await remove(`/documents/${docId}/consumers/${consumer.id}`);
            setConsumers(consumers.filter((consumer) => consumer.id !== consumer.id));
            toast.success(`Owner succesfully updated.`, toastConfig);
        } catch (error) {
            console.error('Error:', error);
            toast.error(`${docName} failed to update.`, toastConfig);
        }
        setLoading(false);
    };

    const docOperations: {
        [key: string]: (fields: FormField[], fieldPath: string[], newValue: any, setChanges?: any, recurse?: boolean | undefined) => FormField[];
    } = {
        updateFormFieldValue,
        insertSubSchema,
        removeSubSchema,
        createListItem,
        readListItem,
        updateListItem,
        removeListItem,
        clearListSchema,
        addField,
        editField,
        deleteField,
        updateFieldOrder
    };

    const updateSchema = async (value: any, fieldPath: string[], fieldName: string, operation: string) => {
        const fullPath = [...fieldPath, fieldName];
        return new Promise<void>(resolve => {
            setFormFields(prevFields => {
                const updatedFields = docOperations[operation](prevFields, fullPath, value, setChanges, false);
                return updatedFields;
            });
            // Resolve the promise after the state is updated
            resolve();
        });
    };

    // Generate sub schema for object
    const generateSubSchema = async (fieldName: string, fieldPath: string[], type: string) => {
        try {
            setLoading(true);

            // Add current field to path
            const fullPath = [...fieldPath, fieldName];
            const currentField = getFieldByPath(formFields, fullPath);

            const response = await post(`/doc-gpt/generate-schema/object/`, {
                document: { name: docName, description: docDescription },
                field: currentField,
                field_path: fullPath,
                doc_schema: formFields
            });

            const subSchema = initializeFormFields(JSON.parse(response.data).schema);

            await updateSchema(subSchema, fieldPath, fieldName, 'insertSubSchema');
            setNeedsUpdate(true);

        } catch (error) {
            console.error('Error updating product:', error);
        }
        setLoading(false);
        setNewDoc(false)
    };

    // Show modal if id is "new"
    useEffect(() => {
        fetchDocumentation(document);
        setDocId(document.id);
    }, [document]);

    useEffect(() => {
        if (needsUpdate) {
            handleSubmit();
            setNeedsUpdate(false);
        }
    }, [needsUpdate]);

    useEffect(() => {
        if (ownerNeedsUpdate && owner) {
            updateOwner(owner);
            setOwnerNeedsUpdate(false);
        }
    }, [ownerNeedsUpdate]);

    return (
        <div className="grow h-full">
            {loading && (
                <div className="absolute inset-0 z-55 flex justify-center items-center bg-white bg-opacity-50">
                    <div className="w-12 h-12 mt-4 mb-4 rounded-full animate-spin border-y-2 border-solid border-gray-900 border-t-transparent"></div>
                </div>
            )}
            <ToastContainer />
            {fetching === false && (
                        <div className="flex-grow overflow-hidden">
                            <div className="items-center justify-center px-8 bg-gray-50 overflow-y-auto max-h-[calc(100vh-1px)] min-h-screen scrollbar-thin shadow-lg">
                                <div className="items-center justify-center w-full h-full">
                                    <div className="flex flex-col h-screen">
                                        <div className=''>
                                            <div className="">
     
                                                <div className="space-y-12 p-8">
                                                    <div className="border-b border-gray-900/10">

                                                        {docForm && <DocForm docName={docName} docDescription={docDescription} docId={docId} docForm={docForm} status={status} setDocForm={setDocForm} handleSubmit={handleSubmit} owner={owner} setOwner={setOwner} setOwnerNeedsUpdate={setOwnerNeedsUpdate} consumers={consumers} addConsumer={addConsumer} removeConsumer={removeConsumer} setStatus={setStatus} setNeedsUpdate={setNeedsUpdate} setDocDescription={setDocDescription} setDocName={setDocName} loading={loading}/>}

                                                        {toggleEdit ? (
                                                            <SchemaEdit docId={document.id} formFields={formFields} updateSchema={updateSchema} generateSubSchema={generateSubSchema} path={[]} setNeedsUpdate={setNeedsUpdate} />

                                                        ) : (
                                                            <Schema docId={docId} formFields={formFields} updateSchema={updateSchema} generateSubSchema={generateSubSchema} path={[]} setNeedsUpdate={setNeedsUpdate} />
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="pr-8 pl-8 pb-5 sticky top-0 z-20 bg-gray-50 shadow-l flex flex-row justify-between border-b">
            
                                                <div className="flex justify-end pb-16">
                                                    <button
                                                        onClick={() => setNeedsUpdate(true)}
                                                        type="submit"
                                                        className="rounded-md bg-blue-900 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                                    >
                                                        Save
                                                    </button>
                                                </div>
                                            </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
      
            )}
        </div>
        
    );
};

export default DocMini;
